import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { categoryService } from '../../shared/utils/serviceProvider';
import { logAuditEvent, AuditAction, AuditLevel } from '../../shared/utils/auditLogger';
import i18n from '../../i18n';

const ca = (key, opts) => i18n.t(`serviceMessages.categories.${key}`, opts);

// Create the context
const CategoriesContext = createContext();

// Custom hook to use the context
export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error(ca('hook_outside_provider'));
  }
  return context;
};

// Provider component
export const CategoriesProvider = ({ children }) => {
  // State for categories
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
    // Refresh categories function (can be called externally)
  const refreshData = useCallback(async () => {
    setLoading(true);
    let retries = 0;
    const maxRetries = 2;
    
    const fetchData = async () => {
      try {
        // Get categories from service provider
        const categoriesData = await categoryService.getCategories();
        if (categoriesData && Array.isArray(categoriesData)) {
          // Filter out any corrupted records
          const validCategories = categoriesData.filter(cat => cat && cat.id);
          
          // Map server response to frontend format
          const formattedCategories = validCategories.map(cat => ({
            id: cat.id,
            name: cat.name || ca('untitled'),
            type: cat.type || 'product',
            description: cat.description || '',
            prefix: cat.prefix || '',
            color: cat.color || '#000000',
            active: cat.active === 1 || cat.active === true,
            productCount: cat.product_count || 0
          }));
          
          setCategories(formattedCategories);
          console.log(`Refreshed ${formattedCategories.length} categories from database`);
          return true;
        } else {
          console.log('No categories found in database during refresh or invalid data received');
          setCategories([]);
          return true;
        }
      } catch (error) {
        console.error(`Error refreshing categories from database (attempt ${retries + 1}/${maxRetries + 1}):`, error);
        
        if (retries < maxRetries) {
          retries++;
          console.log(`Retrying refresh in 500ms... (attempt ${retries + 1}/${maxRetries + 1})`);
          await new Promise(resolve => setTimeout(resolve, 500));
          return fetchData(); // Recursive retry
        } else {
          setCategories([]);
          return false;
        }
      }
    };
    
    await fetchData();
    setLoading(false);
  }, []);

  // Load categories from database on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Get categories from service provider
        const categoriesData = await categoryService.getCategories();
        if (categoriesData && Array.isArray(categoriesData)) {
          // Map server response to frontend format
          const formattedCategories = categoriesData.map(cat => ({
            id: cat.id,
            name: cat.name,
            type: cat.type,
            description: cat.description || '',
            prefix: cat.prefix || '',
            color: cat.color || '#3b82f6',
            active: cat.active === 1 || cat.active === true,
            productCount: cat.product_count || 0
          }));
          
          setCategories(formattedCategories);
          console.log(`Loaded ${categoriesData.length} categories from database`);
        } else {
          console.log('No categories found in database');
          setCategories([]);
        }
      } catch (error) {
        console.error('Error loading categories from database:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Listen for refresh events from sidebar
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Categories context received refresh event');
      refreshData();
    };

    // Listen for general refresh events
    window.addEventListener('refresh-categories', handleRefresh);
    window.addEventListener('refresh-all-data', handleRefresh);
    window.addEventListener('refresh-section', (e) => {
      if (e.detail?.section === 'categories') {
        handleRefresh();
      }
    });

    return () => {
      window.removeEventListener('refresh-categories', handleRefresh);
      window.removeEventListener('refresh-all-data', handleRefresh);
      window.removeEventListener('refresh-section', handleRefresh);
    };
  }, [refreshData]);

  // We don't need to save categories back to database on every change
  // as each operation will directly update the database

  // Add a new category
  const addCategory = async (category) => {
    try {
      // Prepare category data for API
      const categoryData = {
        name: category.name,
        type: category.type || 'product', // Default to product if not specified
        description: category.description || '',
        prefix: category.prefix || '',
        color: category.color || '#3b82f6', // Default color if not specified
        product_count: category.productCount || 0
      };
      
      // Add to database using service provider
      const result = await categoryService.createCategory(categoryData);
      
      // Map the response to frontend format
      const newCategory = {
        id: result.id,
        name: result.name,
        type: result.type || 'product',
        description: result.description || '',
        prefix: result.prefix || '',
        color: result.color || '#3b82f6',
        active: result.active === 1 || result.active === true,
        productCount: result.product_count || 0
      };
      
      // Update local state
      const updatedCategories = [...categories, newCategory];
      setCategories(updatedCategories);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.CATEGORY_CREATED, newCategory);
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
      
      // Log audit event
      logAuditEvent(
        'category_create',
        ca('audit_category', { name: newCategory.name }),
        ca('audit_create_detail', { type: newCategory.type }),
        AuditLevel.SUCCESS
      );
      
      return newCategory;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  };

  // Update an existing category
  const updateCategory = async (id, updatedCategory) => {
    try {
      const oldCategory = categories.find(category => category.id === id);
      if (!oldCategory) {
        throw new Error(ca('not_found', { id }));
      }
      
      // Prepare data for API
      const categoryData = {
        name: updatedCategory.name !== undefined ? updatedCategory.name : oldCategory.name,
        type: updatedCategory.type !== undefined ? updatedCategory.type : oldCategory.type,
        description: updatedCategory.description !== undefined ? updatedCategory.description : oldCategory.description,
        prefix: updatedCategory.prefix !== undefined ? updatedCategory.prefix : oldCategory.prefix,
        color: updatedCategory.color !== undefined ? updatedCategory.color : oldCategory.color,
        product_count: updatedCategory.productCount !== undefined ? updatedCategory.productCount : oldCategory.productCount,
        active: updatedCategory.active !== undefined ? updatedCategory.active : oldCategory.active
      };
      
      // Update category using service provider
      const result = await categoryService.updateCategory(id, categoryData);
      
      // Map the response to frontend format
      const updatedCategoryData = {
        ...oldCategory,
        ...updatedCategory,
        id: result.id || id,
        name: result.name,
        type: result.type,
        description: result.description || oldCategory.description || '',
        prefix: result.prefix || oldCategory.prefix || '',
        color: result.color || oldCategory.color || '#3b82f6',
        productCount: result.product_count || oldCategory.productCount || 0,
        active: result.active === 1 || result.active === true
      };
      
      // Update local state
      const newCategories = categories.map(category => 
        category.id === id ? updatedCategoryData : category
      );
      
      setCategories(newCategories);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.CATEGORY_UPDATED, {
        oldCategory,
        newCategory: updatedCategoryData
      });
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
      
      // Log audit event
      logAuditEvent(
        'category_update',
        `Cat�gorie: ${updatedCategoryData.name}`,
        ca('audit_update_detail', { id }),
        AuditLevel.INFO
      );
      
      return updatedCategoryData;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  // Delete a category
  const deleteCategory = async (id) => {
    try {
      // Find the category first
      const categoryToDelete = categories.find(category => category.id === id);
      if (!categoryToDelete) {
        console.warn(`Category with id ${id} not found, possibly already deleted`);
        // No need to throw, just update the local state to reflect this fact
        setCategories(prev => prev.filter(category => category.id !== id));
        return { success: true, alreadyDeleted: true };
      }
      
      console.log(`Deleting category ${categoryToDelete.name} (ID: ${id})`);
      
      // First update local state immediately for better UI responsiveness
      const newCategories = categories.filter(category => category.id !== id);
      setCategories(newCategories);
      
      // Then delete from database using service provider
      await categoryService.deleteCategory(id);
      
      // Emit events after successful deletion
      eventEmitter.emit(EVENT_TYPES.CATEGORY_DELETED, categoryToDelete);
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
      
      // Log audit event
      logAuditEvent(
        'category_delete',
        `Cat�gorie: ${categoryToDelete.name}`,
        ca('audit_delete_detail', { id }),
        AuditLevel.WARNING
      );
      
      // Verify deletion was successful by trying to get the category again
      try {
        const checkCategory = await categoryService.getCategory(id);
        if (checkCategory && checkCategory.id === id) {
          console.warn(`Warning: Category ${id} still exists after deletion. Forcing refresh...`);
          // Force a refresh to get the current state from database
          refreshData();
        } else {
          console.log(`Category ${id} successfully deleted from database`);
        }
      } catch (verifyError) {
        // Error typically means the category doesn't exist, which is expected
        console.log(`Verified category ${id} doesn't exist (expected)`, verifyError.message);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting category:', error);
      
      // Refresh the data from backend to ensure UI is consistent with database
      setTimeout(() => {
        refreshData();
      }, 500);
      
      throw error;
    }
  };

  // Increment product count for a category
  const incrementProductCount = async (categoryName) => {
    try {
      const categoryIndex = categories.findIndex(cat => cat.name === categoryName);
      if (categoryIndex !== -1) {
        const category = categories[categoryIndex];
        const updatedCategory = {
          ...category,
          productCount: (category.productCount || 0) + 1
        };
        
        // Update in database
        await updateCategory(category.id, updatedCategory);
      }
    } catch (error) {
      console.error(`Error incrementing product count for ${categoryName}:`, error);
    }
  };

  // Decrement product count for a category
  const decrementProductCount = async (categoryName) => {
    try {
      const categoryIndex = categories.findIndex(cat => cat.name === categoryName);
      if (categoryIndex !== -1 && categories[categoryIndex].productCount > 0) {
        const category = categories[categoryIndex];
        const updatedCategory = {
          ...category,
          productCount: category.productCount - 1
        };
        
        // Update in database
        await updateCategory(category.id, updatedCategory);
      }
    } catch (error) {
      console.error(`Error decrementing product count for ${categoryName}:`, error);
    }
  };

  // Get category by name
  const getCategoryByName = (name) => {
    return categories.find(cat => cat.name === name);
  };

  // Get category by prefix
  const getCategoryByPrefix = (prefix) => {
    return categories.find(cat => cat.prefix === prefix);
  };

  return (
    <CategoriesContext.Provider
      value={{
        categories,
        loading,
        addCategory,
        updateCategory,
        deleteCategory,
        getCategoryByName,
        getCategoryByPrefix,
        incrementProductCount,
        decrementProductCount,
        refreshData
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export default CategoriesContext; 

